import { debounce } from "lodash";

/** Short entrance so a chart can finish before the next one starts. */
export const CHART_ANIMATION_MS = 280;

export function applyChartMotion(option, enabled = true) {
  if (!option) return option;
  option.animation = !!enabled;
  option.animationDuration = enabled ? CHART_ANIMATION_MS : 0;
  option.animationEasing = "cubicOut";
  option.animationDurationUpdate = enabled ? 180 : 0;
  return option;
}

/**
 * Resolve after the entrance animation, so the next chart is not built
 * while this one is still moving. Timeout covers a chart that never fires.
 */
export function resolveWhenDrawn(chart, resolve, value) {
  const payload = value === undefined ? chart : value;
  let settled = false;
  const done = () => {
    if (settled) return;
    settled = true;
    if (chart && typeof chart.off === "function") chart.off("finished", done);
    resolve(payload);
  };
  if (chart && typeof chart.on === "function") chart.on("finished", done);
  setTimeout(done, CHART_ANIMATION_MS + 120);
}

/**
 * Wait until the browser has actually painted. A same-frame yield still
 * lets the next chart start during the previous animation.
 */
export function yieldToMain() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function isChartInView(el) {
  if (!el || !el.getBoundingClientRect) return true;
  const rect = el.getBoundingClientRect();
  // display:none collapses to 0×0. An empty but on-screen chart still has width.
  if (!rect.width && !rect.height) return false;
  const height = window.innerHeight || document.documentElement.clientHeight;
  const margin = 180;
  const bottom = rect.bottom || rect.top;
  return bottom > -margin && rect.top < height + margin;
}

/**
 * Draw a few charts at a time, and only the ones on screen.
 * The rest start when they scroll into view, still capped at `batchSize`.
 * Returns a stop function for the next render or unmount.
 */
export function scheduleCharts(jobs, { batchSize = 3 } = {}) {
  const queue = [];
  (jobs || []).forEach((job) => {
    if (!job || typeof job.run !== "function" || !job.el) return;
    queue.push({
      el: job.el,
      run: job.run,
      visible: isChartInView(job.el),
      started: false,
    });
  });

  let active = 0;
  let stopped = false;

  const pump = () => {
    if (stopped || active >= batchSize) return;
    const job = queue.find((item) => item.visible && !item.started);
    if (!job) return;
    job.started = true;
    active += 1;
    // One new chart per frame, so several can animate together
    // without stacking their init work in the same turn.
    requestAnimationFrame(() => {
      if (!stopped) pump();
    });
    Promise.resolve()
      .then(() => job.run())
      .catch(() => {})
      .finally(() => {
        active -= 1;
        if (!stopped) pump();
      });
  };

  const observer = typeof IntersectionObserver === "undefined"
    ? null
    : new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const job = queue.find((item) => item.el === entry.target);
        if (!job || job.started) return;
        job.visible = true;
      });
      pump();
    }, { root: null, rootMargin: "180px 0px", threshold: 0.01 });

  queue.forEach((job) => {
    if (observer) observer.observe(job.el);
  });

  requestAnimationFrame(() => requestAnimationFrame(pump));

  return function stop() {
    stopped = true;
    if (observer) observer.disconnect();
  };
}


/**
 * One debounced resize per chart. Raw window listeners fire dozens of times
 * while the window is dragged and redraw every dashboard chart each time.
 * Removed when the chart is disposed.
 */
export function bindChartResize(chart) {
  if (!chart || chart.__resizeBound) return;
  chart.__resizeBound = true;

  const onResize = debounce(() => {
    if (chart.isDisposed && chart.isDisposed()) return;
    chart.resize();
  }, 150);

  window.addEventListener("resize", onResize);
  const dispose = chart.dispose.bind(chart);
  chart.dispose = function () {
    window.removeEventListener("resize", onResize);
    if (onResize.cancel) onResize.cancel();
    return dispose();
  };
}

/**
 * Touch/tap tooltips for ECharts.
 *
 * Hover tooltips rely on mousemove; on phones that never fires (or fires and
 * immediately clears). Passive touch listeners in main.js also stop ECharts
 * from owning the gesture. Explicit showTip on zr click fixes both.
 */
export function enableTouchTooltip(chart) {
  if (!chart || chart.__touchTooltipBound) return;
  chart.__touchTooltipBound = true;

  const el = chart.getDom && chart.getDom();
  if (el) el.style.touchAction = "manipulation";

  chart.getZr().on("click", (e) => {
    if (e.target && e.target.silent) return;
    chart.dispatchAction({
      type: "showTip",
      x: e.offsetX,
      y: e.offsetY,
    });
  });
}
